// src/components/quizzes/BonusSideScroller.js
//
// ONE side-scrolling catch game. Car, fish, rocket and plane are themes.
//
// (Nov 2026) 🏁 End game button — keep the points, no star
// (onBonusComplete(score, { endedEarly })) — and the read-aloud starts
// straight away at a quicker pace (kit/bonusVoice.js).
//
// ===========================================================================
// WHAT THIS REPLACES
// ===========================================================================
// BonusCarRace.js, BonusFishWorld.js and BonusRocketRace.js were three copies
// of BonusPlaneMode.js at ~440 lines each, differing in a noun. Two of them
// were still carrying the other one's comments ("same as BonusFishWorld" in
// BonusFishWorld.js, referring to itself).
//
// ===========================================================================
// THE THREE CLONES WERE BROKEN, NOT JUST DUPLICATED
// ===========================================================================
//
//   1. THE IMAGES DID NOT EXIST. All three shipped:
//
//        { key: "yellow", url: "car-yellow.png", points: 5 }
//
//      A bare relative path, resolved against whatever route the child was on
//      — /topics/nc-1st-science/car-yellow.png — which 404s. So the "fish"
//      game and the "rocket" game were both rendering broken-image icons of a
//      car that was never uploaded. They were unplayable. Only PlaneMode had
//      real Storage URLs, because it was the original.
//
//      Fixed by not depending on art that doesn't exist: every theme except
//      plane draws its sprite as an emoji on a coloured pod. Plane keeps its
//      real Storage images, which do exist.
//
//   2. THE DECOYS WERE FREE. Red, blue and green were configured `points: -1`,
//      but handleClick read:
//
//        if (item.<theme>.points > 0) { setBonusScore(...) }
//
//      so a negative never applied. Tapping a wrong item cost nothing, which
//      makes "catch only the yellow ones" the same game as "tap everything".
//      Decoys now actually cost you, and break your combo.
//
//   3. DOUBLE-APPLIED POSITION. The clones set `top: ${item.top}%` in `style`
//      AND `y: ${item.top}%` in the motion initial/animate. The percentage in
//      a transform resolves against the ELEMENT's own height, not the
//      viewport, so a "70%" row landed at 70px, not 70% down the screen.
//      Everything bunched into a band near the top. (PlaneMode had already
//      dropped the y, which is why it looked right and the clones didn't.)
//
//   4. THE SAME TWO BUGS AS THE OLD DIAMOND ROUND. Items that flew off-screen
//      uncaught were never removed from state, and a single shared Audio
//      element meant fast tapping went silent. Both fixed the same way here.
//
//   5. PlaneMode used `window.innerWidth` inside render, so rotating a tablet
//      or resizing mid-round left sprites flying to the wrong offsets. Uses
//      viewport units now.
//
// ===========================================================================
// USAGE
// ===========================================================================
//   <BonusSideScroller theme="car"  onBonusComplete={fn} placeholderImage={s} />
//
// BonusCarRace.js / BonusFishWorld.js / BonusRocketRace.js / BonusPlaneMode.js
// are now four-line wrappers around this, so BonusRouter needs no change.

import React, { useState, useEffect, useRef, useCallback } from "react";
import { playGameSound } from "../../services/gameSounds";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { bonusSay, bonusHush } from "./kit/bonusVoice";
import BonusEndEarly from "./kit/BonusEndEarly";

// ---------------------------------------------------------------------------
// Tuning — one place for anything a teacher would argue about
// ---------------------------------------------------------------------------

const TUNING = {
  ROUND_SECONDS: 30,
  SPAWN_MS: 620,
  SPAWN_MS_FRENZY: 380,
  FRENZY_AT: 5,
  TARGET_POINTS: 5,
  DECOY_POINTS: -3,
  TARGET_WEIGHT: 0.62,       // chance a spawn is the one you want
  COMBO_TIERS: [
    { at: 10, multiplier: 3, label: "TRIPLE!" },
    { at: 5, multiplier: 2, label: "DOUBLE!" },
  ],
};

// ---------------------------------------------------------------------------
// Themes
// ---------------------------------------------------------------------------

const POD_COLORS = {
  gold:  { bg: "linear-gradient(180deg,#ffe066,#f5a623)", ring: "#c97d0d", glow: "rgba(255,205,60,.95)" },
  red:   { bg: "linear-gradient(180deg,#ff8b8b,#e03131)", ring: "#a51111", glow: "rgba(255,110,110,.85)" },
  blue:  { bg: "linear-gradient(180deg,#74c0fc,#1c7ed6)", ring: "#0b559e", glow: "rgba(90,180,255,.85)" },
  green: { bg: "linear-gradient(180deg,#8ce99a,#2f9e44)", ring: "#1b6b2b", glow: "rgba(110,230,140,.85)" },
};

const PLANE_IMAGES = {
  gold: {
    left:  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/gameimages%2Fbonusround-images%2Fyellow-plane-left.png?alt=media&token=b0c58e74-f1f4-4003-9e23-a0089fc128c7",
    right: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/gameimages%2Fbonusround-images%2Fyellow-plane.png?alt=media&token=2e6340db-dc8a-4092-ab98-2a9ffeeb8090",
  },
  red: {
    left:  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/gameimages%2Fbonusround-images%2Fred-plane-left.png?alt=media&token=bb70e08b-df3a-482d-a4c5-aa74df6ad482",
    right: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/gameimages%2Fbonusround-images%2Fred-plane.png?alt=media&token=ef37f2ce-02ef-4a00-aee3-e5c9c95edddd",
  },
  blue: {
    left:  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/gameimages%2Fbonusround-images%2Fblue-plane-left.png?alt=media&token=aca5c7e1-c777-44b0-bffc-ffcea58eb1d4",
    right: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/gameimages%2Fbonusround-images%2Fblue-plane.png?alt=media&token=b23b597d-3710-4059-aedf-6ef70ed29c67",
  },
  green: {
    left:  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/gameimages%2Fbonusround-images%2Fgreen-plane-left.png?alt=media&token=a9e12571-d16d-4fdb-ac98-4e10a269982d",
    right: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/gameimages%2Fbonusround-images%2Fgreen-plane.png?alt=media&token=11e387a3-3ea6-40de-ac84-1abdb4a5bd4c",
  },
};

export const THEMES = {
  car: {
    label: "Race Cars",
    icon: "🏎️",
    noun: "car",
    plural: "cars",
    emoji: "🏎️",
    minSpeed: 5.5,
    maxSpeed: 8.5,
  },
  fish: {
    label: "Fish World",
    icon: "🐠",
    noun: "fish",
    plural: "fish",
    emoji: "🐠",
    minSpeed: 6.5,
    maxSpeed: 10,
  },
  rocket: {
    label: "Rocket Race",
    icon: "🚀",
    noun: "rocket",
    plural: "rockets",
    emoji: "🚀",
    minSpeed: 4.5,
    maxSpeed: 7,
  },
  plane: {
    label: "Plane Mode",
    icon: "✈️",
    noun: "plane",
    plural: "planes",
    // Plane is the one theme with real uploaded art, so it uses images.
    images: PLANE_IMAGES,
    minSpeed: 7,
    maxSpeed: 11,
  },
};

const DECOY_COLORS = ["red", "blue", "green"];

// ---------------------------------------------------------------------------
// Audio
// ---------------------------------------------------------------------------

const TAP_SOUND_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/sounds%2Ftapsound1.mp3?alt=media&token=c60530e0-337c-4481-ae9c-87a64c076686";
const CHIME_SOUND_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/sounds%2Fbongsound1.mp3?alt=media&token=f47df164-06b3-494e-ba16-5ab54f707306";
const MUSIC_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/gamemusic%2Fbonus-round1.mp3?alt=media&token=e4c606bb-90ca-4a78-9f2e-34a709084464";
const LOGO_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2FSBLLogoBlackTM.png?alt=media&token=2f8fde38-b373-4367-9ade-a7b68bae3b6f";
const TALK_BTN_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Ftalktextbutton.png?alt=media&token=c3a19304-b98a-43cf-9d81-c9b80000375e";
const MUTE_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fmute-button.png?alt=media&token=9b91203e-011f-4324-bbea-300d0d273fcf";
const UNMUTE_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Funmute-button.png?alt=media&token=e6afbbd2-41e5-41c1-b911-513fa41a4360";

/** Round-robin pool so rapid taps overlap rather than cutting each other off. */
const makePool = (url, size = 5) => {
  const pool = Array.from({ length: size }, () => new Audio(url));
  let i = 0;
  return () => {
    const a = pool[i];
    i = (i + 1) % size;
    try {
      a.currentTime = 0;
      a.play().catch(() => {});
    } catch {
      /* autoplay policy */
    }
  };
};

const randomId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;

// ---------------------------------------------------------------------------

export default function BonusSideScroller({
  theme = "car",
  onBonusComplete,
  placeholderImage,
}) {
  const cfg = THEMES[theme] || THEMES.car;

  const [bonusScore, setBonusScore] = useState(0);
  const [items, setItems] = useState([]);
  const [pops, setPops] = useState([]);
  const [timer, setTimer] = useState(TUNING.ROUND_SECONDS);
  const [isFinished, setIsFinished] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [countdown, setCountdown] = useState(null);

  const [combo, setCombo] = useState(0);
  const [bestCombo, setBestCombo] = useState(0);
  const [caught, setCaught] = useState(0);
  const [comboFlash, setComboFlash] = useState(null);
  const [screenShake, setScreenShake] = useState(false);
  const [paused, setPaused] = useState(false); // the End game question is up
  const [endedEarly, setEndedEarly] = useState(false);
  const sentRef = useRef(false);

  const bonusScoreRef = useRef(0);
  useEffect(() => {
    bonusScoreRef.current = bonusScore;
  }, [bonusScore]);

  const musicRef = useRef(null);
  const spawnRef = useRef(null);
  const playTap = useRef(makePool(TAP_SOUND_URL)).current;
  const playChime = useRef(makePool(CHIME_SOUND_URL, 3)).current;

  const INTRO_SCRIPT =
    `Catch the gold ${cfg.plural} as they fly past. Gold is worth five points. ` +
    `Red, blue and green cost you three, so leave those alone. ` +
    `Catch five gold in a row for double points. ` +
    `Click the yellow Start button when you are ready.`;

  const multiplierFor = (streak) => {
    for (const tier of TUNING.COMBO_TIERS) if (streak >= tier.at) return tier;
    return { multiplier: 1, label: null };
  };
  const activeTier = multiplierFor(combo);

  // =========================================================================
  // TTS — same engine as the other bonus rounds
  // =========================================================================
  const canSpeak = typeof window !== "undefined" && "speechSynthesis" in window;
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [, setVoices] = useState([]);
  const [selectedVoiceName, setSelectedVoiceName] = useState(
    localStorage.getItem("ttsVoiceName") || ""
  );
  const [userActivated, setUserActivated] = useState(false);

  const PREFERRED_VOICE_PATTERNS = [
    /Microsoft .*? (Aria|Jenny|Guy) .*? Online .*? Natural/i,
    /Google US English/i,
    /Samantha/i, /Victoria/i, /Daniel/i,
    /Microsoft Zira/i, /Microsoft David/i, /Microsoft Mark/i,
  ];

  const chooseBestVoice = (list) => {
    for (const pat of PREFERRED_VOICE_PATTERNS) {
      const v = list.find((v) => pat.test(v.name));
      if (v) return v;
    }
    return (
      list.find((v) => /en(-|_)?US/i.test(v.lang)) ||
      list.find((v) => /^en/i.test(v.lang)) ||
      list[0] ||
      null
    );
  };

  const loadVoices = useCallback(() => {
    const list = window.speechSynthesis.getVoices() || [];
    setVoices(list);
    if (!selectedVoiceName && list.length) {
      const best = chooseBestVoice(list);
      if (best) {
        setSelectedVoiceName(best.name);
        localStorage.setItem("ttsVoiceName", best.name);
      }
    }
  }, [selectedVoiceName]);

  const waitForVoices = (maxWaitMs = 1200) =>
    new Promise((resolve) => {
      const start = Date.now();
      const check = () => {
        const list = window.speechSynthesis?.getVoices?.() || [];
        if (list.length || Date.now() - start > maxWaitMs) return resolve(list);
        setTimeout(check, 100);
      };
      check();
    });

  const unlockTTS = async () => {
    try { window.speechSynthesis?.resume?.(); } catch {}
    try {
      const u = new SpeechSynthesisUtterance(" ");
      u.volume = 0;
      window.speechSynthesis.speak(u);
      window.speechSynthesis.cancel();
    } catch {}
  };

  useEffect(() => {
    const onFirstGesture = async () => {
      setUserActivated(true);
      await unlockTTS();
      await waitForVoices();
    };
    window.addEventListener("pointerdown", onFirstGesture, { once: true, passive: true });
    return () => window.removeEventListener("pointerdown", onFirstGesture);
  }, []);

  useEffect(() => {
    if (!canSpeak) return;
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, [canSpeak, loadVoices]);

  // Quick and immediate (kit/bonusVoice.js): 1.15 speed, no waiting.
  const speakText = async (text) => {
    if (!canSpeak || !text) return;
    bonusSay(text, {
      onStart: () => {
        setIsSpeaking(true);
        if (musicRef.current) musicRef.current.volume = 0.1;
      },
      onEnd: () => {
        setIsSpeaking(false);
        if (musicRef.current && !isMuted) musicRef.current.volume = 0.3;
      },
    });
  };

  const stopSpeaking = () => {
    if (!canSpeak) return;
    bonusHush();
    setIsSpeaking(false);
    if (musicRef.current && !isMuted) musicRef.current.volume = 0.3;
  };

  // =========================================================================
  // Music
  // =========================================================================
  useEffect(() => {
    musicRef.current = new Audio(MUSIC_URL);
    musicRef.current.loop = true;
    musicRef.current.volume = 0.3;
    musicRef.current.play().catch(() => {});
    return () => {
      musicRef.current?.pause();
      if (musicRef.current) musicRef.current.currentTime = 0;
    };
  }, []);

  // Read the directions straight away (it used to wait for another tap);
  // if the browser blocked that, the first tap starts it.
  const introSpoken = useRef(false);
  useEffect(() => {
    const t = setTimeout(() => {
      if (!introSpoken.current && showIntro) {
        introSpoken.current = true;
        speakText(INTRO_SCRIPT);
      }
    }, 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (userActivated && showIntro && !isSpeaking && !introSpoken.current) {
      introSpoken.current = true;
      speakText(INTRO_SCRIPT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userActivated]);

  // =========================================================================
  // Round flow
  // =========================================================================

  const beginCountdown = () => {
    stopSpeaking();
    setShowIntro(false);
    setCountdown(3);
  };

  useEffect(() => {
    if (countdown === null) return;
    if (countdown === "GO!") {
      const t = setTimeout(() => setCountdown(null), 550);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setCountdown((c) => (c > 1 ? c - 1 : "GO!")), 700);
    return () => clearTimeout(t);
  }, [countdown]);

  const isFrenzy = !isFinished && timer <= TUNING.FRENZY_AT && timer > 0;

  useEffect(() => {
    if (showIntro || countdown !== null || isFinished || paused) return;

    const rate = isFrenzy ? TUNING.SPAWN_MS_FRENZY : TUNING.SPAWN_MS;

    spawnRef.current = setInterval(() => {
      const isTarget = Math.random() < TUNING.TARGET_WEIGHT;
      const color = isTarget
        ? "gold"
        : DECOY_COLORS[Math.floor(Math.random() * DECOY_COLORS.length)];

      setItems((prev) => [
        ...prev,
        {
          id: randomId(),
          color,
          isTarget,
          // Kept clear of the HUD strip at the top and the footer at the
          // bottom. The old version allowed 0-80% and sprites sat under the
          // score card where they couldn't be tapped.
          top: 22 + Math.random() * 52,
          speed: cfg.minSpeed + Math.random() * (cfg.maxSpeed - cfg.minSpeed),
          direction: Math.random() > 0.5 ? "left" : "right",
        },
      ]);
    }, rate);

    return () => clearInterval(spawnRef.current);
  }, [showIntro, countdown, isFinished, isFrenzy, cfg.minSpeed, cfg.maxSpeed, paused]);

  useEffect(() => {
    if (showIntro || countdown !== null || isFinished || paused) return;

    const t = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(t);
          if (spawnRef.current) {
            clearInterval(spawnRef.current);
            spawnRef.current = null;
          }
          setIsFinished(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(t);
  }, [showIntro, countdown, isFinished, paused]);

  // Score back to the parent after the summary (or now, with Continue). An
  // early end keeps the points but isn't counted as finishing (no star).
  const sendScore = useCallback(() => {
    if (sentRef.current) return;
    sentRef.current = true;
    onBonusComplete?.(Math.max(0, bonusScoreRef.current), { endedEarly });
  }, [onBonusComplete, endedEarly]);

  useEffect(() => {
    if (!isFinished) return;
    const finalScore = bonusScoreRef.current;
    if (finalScore > 0 && !endedEarly) {
      confetti({ spread: 100, particleCount: 120, startVelocity: 50, origin: { y: 0.5 } });
    }
    const t = setTimeout(sendScore, endedEarly ? 4200 : 3600);
    return () => clearTimeout(t);
  }, [isFinished, endedEarly, sendScore]);

  const endEarly = () => {
    if (spawnRef.current) {
      clearInterval(spawnRef.current);
      spawnRef.current = null;
    }
    setEndedEarly(true);
    setIsFinished(true);
  };

  useEffect(() => {
    return () => {
      clearInterval(spawnRef.current);
      stopSpeaking();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =========================================================================
  // Tapping
  // =========================================================================

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const addPop = (text, left, top, kind) => {
    const id = randomId();
    setPops((prev) => [...prev, { id, text, left, top, kind }]);
    setTimeout(() => setPops((prev) => prev.filter((p) => p.id !== id)), 900);
  };

  const handleTap = (item, event) => {
    if (isFinished || paused) return;
    removeItem(item.id);

    const leftPct = event?.clientX ? (event.clientX / window.innerWidth) * 100 : 50;

    if (!item.isTarget) {
      playGameSound("bonusWrong", "sideScroller", playTap);
      setCombo(0);
      setScreenShake(true);
      setTimeout(() => setScreenShake(false), 400);
      // Floored at zero. A child must never hand a negative bonus score to
      // the leaderboard — the same guard the diamond round now has.
      setBonusScore((prev) => Math.max(0, prev + TUNING.DECOY_POINTS));
      addPop(String(TUNING.DECOY_POINTS), leftPct, item.top, "bad");
      return;
    }

    const nextCombo = combo + 1;
    const tier = multiplierFor(nextCombo);
    const awarded = TUNING.TARGET_POINTS * tier.multiplier;

    setCombo(nextCombo);
    setBestCombo((b) => Math.max(b, nextCombo));
    setCaught((c) => c + 1);
    setBonusScore((prev) => prev + awarded);
    addPop(`+${awarded}`, leftPct, item.top, "good");

    const prevTier = multiplierFor(combo);
    if (tier.label && tier.multiplier > prevTier.multiplier) {
      setComboFlash(tier.label);
      setTimeout(() => setComboFlash(null), 1100);
      playGameSound("bonusPower", "sideScroller", playChime);
    } else {
      playGameSound("bonusGood", "sideScroller", playTap);
    }

    confetti({
      spread: 55,
      scalar: 0.75,
      startVelocity: 34,
      particleCount: 26,
      colors: ["#ffd43b", "#fff3bf", "#f59f00"],
      origin: { x: leftPct / 100, y: item.top / 100 },
    });
  };

  const stars = endedEarly ? 0 : bonusScore >= 150 ? 3 : bonusScore >= 75 ? 2 : bonusScore > 0 ? 1 : 0;
  const timePct = (timer / TUNING.ROUND_SECONDS) * 100;

  const bgImage =
    placeholderImage ||
    "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fpirate-ship-background.png?alt=media&token=3912623d-30d2-468a-ab2e-449398d2bce2";

  /** One sprite: real image for plane, coloured emoji pod for everything else. */
  const renderSprite = (color, direction) => {
    if (cfg.images) {
      const src = cfg.images[color]?.[direction === "left" ? "left" : "right"];
      return <img src={src} alt="" draggable="false" className="ss-img" />;
    }
    const pod = POD_COLORS[color];
    return (
      <span
        className="ss-pod"
        style={{
          background: pod.bg,
          borderColor: pod.ring,
          // Flip the emoji so it faces the way it's travelling.
          transform: direction === "left" ? "scaleX(-1)" : "none",
        }}
      >
        <span className="ss-pod-emoji">{cfg.emoji}</span>
      </span>
    );
  };

  return (
    <>
      <style>{`
        .ss-root {
          position: fixed; inset: 0; overflow: hidden;
          background-image: url('${bgImage}');
          background-size: cover; background-position: center; background-repeat: no-repeat;
          background-color: #0e2440;
          font-family: "Baloo 2","Fredoka","Comic Sans MS","Trebuchet MS",sans-serif;
          user-select: none; -webkit-user-select: none; touch-action: manipulation;
        }
        .ss-root::before {
          content:""; position:absolute; inset:0; pointer-events:none;
          background: radial-gradient(120% 85% at 50% 0%,
            rgba(8,23,41,.08) 0%, rgba(8,23,41,.40) 60%, rgba(8,23,41,.70) 100%);
        }
        .ss-root.shake { animation: ss-shake 380ms ease; }
        @keyframes ss-shake {
          0%,100% { transform: translate(0,0); }
          20% { transform: translate(-9px,4px); }
          40% { transform: translate(8px,-5px); }
          60% { transform: translate(-6px,3px); }
          80% { transform: translate(5px,-2px); }
        }

        .ss-logo {
          position:absolute; top:12px; left:50%; transform:translateX(-50%);
          max-width:110px; width:13vw; min-width:70px; height:auto; z-index:4;
          filter: drop-shadow(0 3px 10px rgba(0,0,0,.5));
        }

        .ss-hud {
          position:absolute; top:14px; left:14px; right:14px; z-index:5;
          display:flex; justify-content:space-between; align-items:flex-start;
          gap:12px; pointer-events:none;
        }
        .ss-card {
          background:rgba(8,23,41,.86); border:3px solid rgba(255,255,255,.28);
          border-radius:18px; padding:10px 16px; color:#fff;
          box-shadow:0 5px 0 rgba(0,0,0,.35); min-width:130px;
        }
        .ss-label { display:block; font-size:12px; letter-spacing:.12em; text-transform:uppercase;
          font-weight:800; color:#ffd873; margin-bottom:2px; }
        .ss-value { font-size:34px; font-weight:900; line-height:1; }
        .ss-value.urgent { color:#ff6b6b; animation: ss-pulse .5s ease-in-out infinite; }
        @keyframes ss-pulse { 0%,100%{transform:scale(1);} 50%{transform:scale(1.16);} }

        .ss-track { margin-top:7px; height:10px; border-radius:999px; background:rgba(0,0,0,.5); overflow:hidden; }
        .ss-fill { height:100%; border-radius:999px; transition:width 1s linear;
          background:linear-gradient(90deg,#4fe39a,#ffd43b); }
        .ss-fill.urgent { background:linear-gradient(90deg,#ff8a5c,#ff3b3b); }

        .ss-combo {
          position:absolute; left:50%; transform:translateX(-50%); top:108px; z-index:5;
          width:min(340px,62vw); pointer-events:none; text-align:center;
        }
        .ss-combo-track { height:14px; border-radius:999px; background:rgba(8,23,41,.8);
          border:3px solid rgba(255,255,255,.3); overflow:hidden; }
        .ss-combo-fill { height:100%; border-radius:999px; transition:width 220ms ease;
          background:linear-gradient(90deg,#ff9f43,#ff5e3a); }
        .ss-combo-text { margin-top:5px; font-size:16px; font-weight:900; color:#fff;
          text-shadow:0 2px 5px rgba(0,0,0,.9); }
        .ss-mult { display:inline-block; margin-left:7px; padding:2px 10px; border-radius:999px;
          background:linear-gradient(180deg,#ffd43b,#f59f00); color:#3d2b00; }

        .ss-flash {
          position:absolute; left:50%; top:36%; transform:translateX(-50%); z-index:8;
          font-size:clamp(40px,8vw,92px); font-weight:900; color:#fff;
          -webkit-text-stroke:3px #b5480b; text-shadow:0 0 40px rgba(255,180,60,.95);
          pointer-events:none; animation: ss-flash 1.1s ease forwards;
        }
        @keyframes ss-flash {
          0%{opacity:0;transform:translateX(-50%) scale(.4) rotate(-9deg);}
          25%{opacity:1;transform:translateX(-50%) scale(1.2) rotate(3deg);}
          40%{transform:translateX(-50%) scale(1) rotate(0);}
          75%{opacity:1;}
          100%{opacity:0;transform:translateX(-50%) scale(1.1) translateY(-40px);}
        }

        .ss-pop {
          position:absolute; z-index:7; pointer-events:none; transform:translateX(-50%);
          font-size:clamp(26px,4vw,48px); font-weight:900; color:#fff;
          -webkit-text-stroke:2px rgba(0,0,0,.55);
          animation: ss-rise 900ms cubic-bezier(.25,.9,.35,1) forwards;
        }
        .ss-pop.good { color:#ffd43b; text-shadow:0 0 24px rgba(255,212,59,.9); }
        .ss-pop.bad  { color:#ff6b6b; }
        @keyframes ss-rise {
          0%{opacity:0;transform:translateX(-50%) translateY(16px) scale(.5);}
          20%{opacity:1;transform:translateX(-50%) translateY(0) scale(1.25);}
          35%{transform:translateX(-50%) translateY(-8px) scale(1);}
          100%{opacity:0;transform:translateX(-50%) translateY(-130px) scale(.9);}
        }

        .ss-item { position:absolute; z-index:3; cursor:pointer; will-change:transform;
          width:110px; height:110px; display:grid; place-items:center; }
        .ss-img { width:100%; height:100%; object-fit:contain; display:block; }
        .ss-pod {
          width:96px; height:96px; border-radius:50%; display:grid; place-items:center;
          border:5px solid; box-shadow:0 6px 18px rgba(0,0,0,.45);
        }
        .ss-pod-emoji { font-size:48px; line-height:1; }

        .ss-countdown { position:fixed; inset:0; z-index:9; display:grid; place-items:center;
          background:rgba(8,23,41,.55); pointer-events:none; }
        .ss-countdown span {
          font-size:clamp(90px,22vw,240px); font-weight:900; color:#ffd43b;
          -webkit-text-stroke:5px #7a4b00; text-shadow:0 0 70px rgba(255,212,59,.9);
          animation: ss-count 700ms cubic-bezier(.34,1.5,.64,1);
        }
        @keyframes ss-count {
          0%{opacity:0;transform:scale(2.4);} 35%{opacity:1;transform:scale(.9);}
          55%{transform:scale(1.05);} 100%{opacity:1;transform:scale(1);}
        }

        .ss-overlay {
          position:fixed; inset:0; z-index:10; display:grid; place-items:center; padding:16px;
          background:rgba(8,23,41,.84); backdrop-filter:blur(4px);
        }
        .ss-panel {
          max-width:620px; width:100%; max-height:92vh; overflow-y:auto;
          background:linear-gradient(180deg,#1b3b66,#0e2440);
          border:5px solid #ffd43b; border-radius:28px;
          box-shadow:0 14px 0 rgba(0,0,0,.4), 0 30px 70px rgba(0,0,0,.6);
          padding:26px 24px; text-align:center; color:#fff;
        }
        .ss-title { font-size:clamp(28px,5vw,44px); font-weight:900; color:#ffd43b;
          margin-bottom:6px; -webkit-text-stroke:1.5px #7a4b00; }
        .ss-sub { font-size:17px; opacity:.9; margin-bottom:16px; }
        .ss-key { display:grid; gap:8px; margin:0 0 18px; }
        .ss-key-row { display:flex; align-items:center; gap:12px; text-align:left;
          background:rgba(0,0,0,.3); border-radius:14px; padding:8px 14px;
          font-size:17px; font-weight:700; }
        .ss-key-row .sw { width:44px; height:44px; flex:0 0 44px; display:grid; place-items:center; }
        .ss-key-row .sw .ss-pod { width:40px; height:40px; border-width:3px; }
        .ss-key-row .sw .ss-pod-emoji { font-size:20px; }
        .ss-key-row .sw img { width:100%; height:100%; object-fit:contain; }
        .ss-key-row .pts { margin-left:auto; font-weight:900; color:#ffd43b; font-size:19px; }
        .ss-key-row .pts.neg { color:#ff8080; }
        .ss-actions { display:flex; gap:14px; align-items:center; justify-content:center; flex-wrap:wrap; }
        .ss-start {
          font-family:inherit; padding:16px 42px; border-radius:18px; border:none; cursor:pointer;
          font-size:26px; font-weight:900; color:#3d2b00;
          background:linear-gradient(180deg,#ffe066,#ffc93c); box-shadow:0 7px 0 #c99a00;
        }
        .ss-start:active { transform:translateY(6px); box-shadow:0 1px 0 #c99a00; }
        .ss-stop { font-family:inherit; padding:12px 20px; border-radius:14px; border:none;
          cursor:pointer; font-size:17px; font-weight:800; color:#fff;
          background:#ff5a5a; box-shadow:0 5px 0 #c02b2b; }
        .ss-talk { background:none; border:none; padding:0; cursor:pointer; line-height:0; }

        .ss-stars { font-size:clamp(38px,7vw,64px); letter-spacing:6px; margin-bottom:8px; }
        .ss-stars .st { display:inline-block; animation: ss-star 500ms cubic-bezier(.34,1.6,.64,1) backwards; }
        .ss-stars .st:nth-child(1){animation-delay:320ms;}
        .ss-stars .st:nth-child(2){animation-delay:520ms;}
        .ss-stars .st:nth-child(3){animation-delay:720ms;}
        .ss-stars .dim { opacity:.22; }
        @keyframes ss-star { from{opacity:0;transform:scale(2.6) rotate(-40deg);} to{opacity:1;transform:none;} }
        .ss-score-label { font-size:14px; letter-spacing:.16em; text-transform:uppercase;
          color:#ffd873; font-weight:800; }
        .ss-score { font-size:clamp(52px,11vw,92px); font-weight:900; line-height:1; }
        .ss-stats { display:flex; justify-content:center; gap:26px; margin-top:18px; flex-wrap:wrap; }
        .ss-stat .n { font-size:30px; font-weight:900; color:#ffd43b; }
        .ss-stat .k { font-size:13px; text-transform:uppercase; letter-spacing:.1em; opacity:.85; }

        .ss-mute { position:fixed; bottom:16px; right:16px; background:none; border:none;
          cursor:pointer; z-index:6; padding:0; }
        .ss-mute img { width:78px; height:90px; filter:drop-shadow(0 5px 12px rgba(0,0,0,.55)); }
        .ss-footer { position:absolute; bottom:0; left:0; right:0; z-index:2; font-size:12px;
          color:rgba(255,255,255,.6); text-align:center; padding:8px 0; }

        @media (max-width:560px) {
          .ss-card { min-width:104px; padding:8px 12px; }
          .ss-value { font-size:26px; }
          .ss-logo { display:none; }
          .ss-combo { top:92px; }
          .ss-item { width:82px; height:82px; }
          .ss-pod { width:72px; height:72px; border-width:4px; }
          .ss-pod-emoji { font-size:34px; }
          .ss-mute img { width:58px; height:66px; }
        }

        @media (prefers-reduced-motion: reduce) {
          .ss-root.shake, .ss-value.urgent, .ss-flash, .ss-countdown span, .ss-stars .st {
            animation: none !important;
          }
        }
      `}</style>

      {/* ---------------- Intro ---------------- */}
      {showIntro && (
        <div className="ss-overlay">
          <div className="ss-panel">
            <div className="ss-title">{cfg.icon} {cfg.label}</div>
            <div className="ss-sub">Catch the gold {cfg.plural} — leave the rest alone!</div>

            <div className="ss-key">
              {["gold", ...DECOY_COLORS].map((color) => (
                <div className="ss-key-row" key={color}>
                  <span className="sw">{renderSprite(color, "right")}</span>
                  <span>
                    {color === "gold"
                      ? `Gold ${cfg.noun} — catch these!`
                      : `${color[0].toUpperCase() + color.slice(1)} ${cfg.noun} — don't tap`}
                  </span>
                  <span className={`pts ${color === "gold" ? "" : "neg"}`}>
                    {color === "gold" ? `+${TUNING.TARGET_POINTS}` : TUNING.DECOY_POINTS}
                  </span>
                </div>
              ))}
              <div className="ss-key-row">
                <span className="sw" style={{ fontSize: 26 }}>🔥</span>
                <span>5 in a row = double points, 10 = triple!</span>
              </div>
            </div>

            <div className="ss-actions">
              <button
                className="ss-talk"
                onClick={async () => {
                  await unlockTTS();
                  await speakText(INTRO_SCRIPT);
                }}
                disabled={!canSpeak}
                aria-label="Read the directions out loud"
              >
                <img
                  src={TALK_BTN_URL}
                  alt="Talk text"
                  style={{ width: 120, height: 120, objectFit: "contain", display: "block" }}
                />
              </button>

              {isSpeaking && <button className="ss-stop" onClick={stopSpeaking}>⏹ Stop</button>}

              <button className="ss-start" onClick={beginCountdown}>Start!</button>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- Countdown ---------------- */}
      {countdown !== null && (
        <div className="ss-countdown">
          <span key={String(countdown)}>{countdown}</span>
        </div>
      )}

      {/* ---------------- Game ---------------- */}
      <div className={`ss-root ${screenShake ? "shake" : ""}`}>
        <img src={LOGO_URL} alt="SBL Logo" className="ss-logo" />

        <div className="ss-hud">
          <div className="ss-card">
            <span className="ss-label">Score</span>
            <div className="ss-value">{bonusScore}</div>
          </div>

          <div className="ss-card" style={{ textAlign: "right" }}>
            <span className="ss-label">Time</span>
            <div className={`ss-value ${isFrenzy ? "urgent" : ""}`}>{timer}s</div>
            <div className="ss-track">
              <div className={`ss-fill ${isFrenzy ? "urgent" : ""}`} style={{ width: `${timePct}%` }} />
            </div>
          </div>
        </div>

        {!showIntro && !isFinished && combo > 0 && (
          <div className="ss-combo">
            <div className="ss-combo-track">
              <div className="ss-combo-fill" style={{ width: `${Math.min(100, (combo / 10) * 100)}%` }} />
            </div>
            <div className="ss-combo-text">
              🔥 {combo} in a row
              {activeTier.multiplier > 1 && <span className="ss-mult">×{activeTier.multiplier}</span>}
            </div>
          </div>
        )}

        {comboFlash && <div className="ss-flash">{comboFlash}</div>}

        <AnimatePresence>
          {!isFinished && !showIntro && countdown === null &&
            items.map((item) => (
              <motion.div
                key={item.id}
                className="ss-item"
                style={{
                  top: `${item.top}%`,
                  left: 0,
                  filter: `drop-shadow(0 0 12px ${POD_COLORS[item.color].glow})`,
                }}
                // Viewport units, not window.innerWidth, so a rotate or
                // resize mid-round doesn't strand sprites off-screen.
                initial={{ x: item.direction === "left" ? "102vw" : "-15vw" }}
                animate={{ x: item.direction === "left" ? "-15vw" : "102vw" }}
                exit={{ scale: 1.6, opacity: 0, transition: { duration: 0.2 } }}
                transition={{ duration: item.speed, ease: "linear" }}
                // Uncaught sprites unmount instead of accumulating in state
                // for the whole round.
                onAnimationComplete={() => removeItem(item.id)}
                onPointerDown={(e) => handleTap(item, e)}
              >
                {renderSprite(item.color, item.direction)}
              </motion.div>
            ))}
        </AnimatePresence>

        {pops.map((p) => (
          <div key={p.id} className={`ss-pop ${p.kind}`} style={{ left: `${p.left}%`, top: `${p.top}%` }}>
            {p.text}
          </div>
        ))}

        <button
          className="ss-mute"
          onClick={() => {
            const next = !isMuted;
            setIsMuted(next);
            if (musicRef.current) musicRef.current.muted = next;
          }}
          aria-label={isMuted ? "Turn music on" : "Turn music off"}
        >
          <img src={isMuted ? UNMUTE_URL : MUTE_URL} alt="" />
        </button>

        <div className="ss-footer">© Smart Board Learning LLC. All rights reserved.</div>
      </div>

      {!showIntro && !isFinished && <BonusEndEarly onEnd={endEarly} onPause={setPaused} />}

      {/* ---------------- Summary ---------------- */}
      {isFinished && (
        <div className="ss-overlay">
          <div className="ss-panel">
            <div className="ss-title">{endedEarly ? "Round ended early" : "Bonus Round Complete!"}</div>

            {!endedEarly && (
              <div className="ss-stars" aria-label={`${stars} out of 3 stars`}>
                {[0, 1, 2].map((i) => (
                  <span key={i} className={`st ${i < stars ? "" : "dim"}`}>⭐</span>
                ))}
              </div>
            )}

            <div className="ss-score-label">Bonus Points</div>
            <div className="ss-score">{bonusScore}</div>

            <div className="ss-stats">
              <div className="ss-stat">
                <div className="n">{caught}</div>
                <div className="k">Caught</div>
              </div>
              <div className="ss-stat">
                <div className="n">{bestCombo}</div>
                <div className="k">Best Streak</div>
              </div>
            </div>
            {endedEarly && <p className="bs-early-note">Your points still count! Play the whole bonus round next time to earn the ⭐ star.</p>}
            <button type="button" className="bs-continue" onClick={sendScore}>
              Continue ▶
            </button>
          </div>
        </div>
      )}
    </>
  );
}
